import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { onAnimationEnd } from '@theme/utilities';

/**
 * <ts-search-modal> — the search overlay's dialog component.
 *
 * Replaces DialogComponent's scroll lock (body position: fixed + top: -scrollY)
 * with overflow: hidden on <html> — the same mechanism the nav drawer uses.
 * The body-fixed lock clamps the document scroll to 0 and re-anchors the
 * sticky header in the same instant, which iOS composites across frames: the
 * top of the screen flashes and any header rect measured around it is a coin
 * flip. With the overflow lock nothing moves on open — the scroll position is
 * untouched and a sticky-pinned header simply stays pinned — so the overlay
 * can dependably sit just below it and there is nothing to restore on close.
 *
 * `--ts-search-overlay-top` places the overlay below a header visible at the
 * top of the viewport (0 = full takeover when there isn't one), measured once
 * per open. Uses its own html attribute for the lock (not [scroll-lock]) so
 * the nav drawer closing mid-open can't release the overlay's lock.
 */
class TsSearchModal extends DialogComponent {
  /**
   * Upper bound on waiting for the close animation to settle.
   * @type {number}
   */
  static #CLOSE_FALLBACK_MS = 300;

  connectedCallback() {
    super.connectedCallback();
    // Escape triggers the native cancel; route it through closeDialog so the
    // close animation runs, the lock releases, and dialog:close still fires.
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('cancel', this.#handleCancel);
    document.documentElement.removeAttribute('data-ts-search-lock');
    document.documentElement.style.removeProperty('--ts-search-scrollbar-gap');
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    // The half-viewport bound is a sanity check, not a layout rule: this
    // theme's header group is 60–100px tall, so a rect ending lower than
    // half the viewport can only be a transient/bogus measurement (the exact
    // reads that used to park the overlay near the bottom on iOS). Reject it
    // and take over the full viewport rather than trusting it — never cap it
    // to a "plausible" value, which would re-trust bad rects.
    // A 'hiding' or 'idle' header measures like a visible one (still pinned)
    // but is invisible or about to be — treat both as "no header".
    const header = document.getElementById('header-component');
    const stickyState = header?.dataset.stickyState;
    const rect = stickyState === 'hiding' || stickyState === 'idle' ? null : header?.getBoundingClientRect();
    const bottom =
      rect && rect.height > 0 && rect.bottom > 0 && rect.bottom < window.innerHeight / 2 ? Math.round(rect.bottom) : 0;
    dialog.style.setProperty('--ts-search-overlay-top', `${bottom}px`);

    // Reserve the scrollbar's width as html padding while the overflow lock
    // hides it (TS-516), so page content keeps its pre-lock width. Padding —
    // not scrollbar-gutter: stable — because the reserved gutter is browser
    // chrome that nothing can paint: a dialog opened over the overlay (Quick
    // View) can't dim it with its ::backdrop, leaving a bright strip beside
    // the modal. The padding strip is ordinary page area the backdrop covers.
    // Measured before the lock removes the scrollbar; overlay scrollbars
    // measure 0 and get no padding.
    const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarGap > 0) {
      document.documentElement.style.setProperty('--ts-search-scrollbar-gap', `${scrollbarGap}px`);
    }
    document.documentElement.setAttribute('data-ts-search-lock', '');
    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.classList.add('dialog-closing');
    // Race the animation settle with a timeout — a hung animation (e.g. an
    // infinite one added to the dialog later) must never leave the modal
    // stuck open with the scroll lock engaged.
    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, TsSearchModal.#CLOSE_FALLBACK_MS)),
    ]);

    dialog.close();
    dialog.classList.remove('dialog-closing');
    document.documentElement.removeAttribute('data-ts-search-lock');
    document.documentElement.style.removeProperty('--ts-search-scrollbar-gap');
    this.dispatchEvent(new DialogCloseEvent());
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };
}

if (!customElements.get('ts-search-modal')) {
  customElements.define('ts-search-modal', TsSearchModal);
}
